// Pure helpers extracted from BoardView so they can be unit tested in isolation.

import { CaseMarker, getCaseMarker } from './caseMarkers.ts';
import { needsNerveBlock } from './staffNameplates.ts';

export const isLatestGeneration = (myGen: number, currentGen: number): boolean => {
  return myGen === currentGen;
};

export const shouldShowRoomInActive = (
  hospitalId: string,
  hasSchedules: boolean,
  forcedActiveHospitalIds: ReadonlySet<string>
): boolean => {
  if (forcedActiveHospitalIds.has(hospitalId)) return true;
  return hasSchedules;
};

export const toggleHospitalIdInSet = (
  set: ReadonlySet<string>,
  hospitalId: string
): Set<string> => {
  const next = new Set(set);
  if (next.has(hospitalId)) {
    next.delete(hospitalId);
  } else {
    next.add(hospitalId);
  }
  return next;
};

export interface ModalCloseState {
  modifiedAllocations: Map<string, unknown>;
  isModifying: boolean;
}

export const computeStateAfterModalClose = (
  current: { isModifying: boolean }
): ModalCloseState | null => {
  // Returns null when no state change is needed (caller should leave existing state alone).
  if (!current.isModifying) return null;
  return {
    modifiedAllocations: new Map(),
    isModifying: false,
  };
};

// ---------------------------------------------------------------------------
// Allocate button enablement
//
// Decides whether the "Allocate Staff" / "Apply Modifications" button is
// disabled. Kept pure so the pin-then-fill flow is unit-testable without
// rendering BoardView.
//
// Rules:
//  - Always disabled while a date isn't chosen, the board is loading, or an
//    allocation run is already in flight.
//  - In modify mode the button stays enabled (re-running with edits is the
//    whole point of modify mode).
//  - If the user has staged in-memory pins (pinCount > 0) the button is ENABLED
//    so they can run the algorithm with those pins — whether the board is empty
//    (pin-then-fill) OR already allocated from a prior run (pin-on-top + re-run;
//    the greedy backend keeps the pins and recomputes the rest idempotently).
//  - Otherwise, once allocations are persisted AND we're not modifying, the
//    button is disabled: re-running with NO pins would just reshuffle the board,
//    so the user must stage a pin, modify, or wipe first.
// ---------------------------------------------------------------------------
export interface AllocateButtonState {
  hasSelectedDate: boolean;
  allocating: boolean;
  loading: boolean;
  hasPersistedAllocations: boolean;
  isModifying: boolean;
  pinCount: number;
}

export const isAllocateDisabled = ({
  hasSelectedDate,
  allocating,
  loading,
  hasPersistedAllocations,
  isModifying,
  pinCount,
}: AllocateButtonState): boolean => {
  if (!hasSelectedDate || allocating || loading) return true;
  // Staged pins enable the run regardless of whether a prior board exists.
  if (pinCount > 0) return false;
  // Day already allocated, no pins, not modifying: lock the button.
  if (hasPersistedAllocations && !isModifying) return true;
  return false;
};

// ---------------------------------------------------------------------------
// Pin modal prefill resolution
//
// Decides what staff the manual-allocation (pin) modal should open pre-filled
// with, for a given schedule. Pure so the pin-on-top + re-run flow is testable.
//
// Priority:
//  1. Modify mode -> the in-progress entry from the modify map (or nothing).
//  2. Pre-run pin flow -> an already-staged in-memory pin for this schedule.
//  3. Otherwise fall back to the schedule's CURRENT persisted allocation, so
//     "Pin & re-run" on an allocated board starts from the staff already on it.
//  4. Nothing to prefill (a brand-new pin on an empty schedule).
// ---------------------------------------------------------------------------
export interface PinAllocationLike {
  scheduleId: string;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
}

interface PersistedAllocationLike {
  operatingRoomScheduleId: string;
  allocatedNurseIds?: string[];
  allocatedDoctorIds?: string[];
}

export const resolvePinPrefill = (args: {
  scheduleId?: string | null;
  isModifying: boolean;
  modifiedAllocations: ReadonlyMap<string, PinAllocationLike>;
  manualAllocations: ReadonlyArray<PinAllocationLike>;
  persistedAllocations: ReadonlyArray<PersistedAllocationLike>;
}): PinAllocationLike | undefined => {
  const { scheduleId, isModifying, modifiedAllocations, manualAllocations, persistedAllocations } = args;
  if (!scheduleId) return undefined;
  if (isModifying) return modifiedAllocations.get(scheduleId);

  const staged = manualAllocations.find(ma => ma.scheduleId === scheduleId);
  if (staged) return staged;

  const persisted = persistedAllocations.find(a => a.operatingRoomScheduleId === scheduleId);
  return persisted
    ? {
        scheduleId,
        allocatedNurseIds: persisted.allocatedNurseIds || [],
        allocatedDoctorIds: persisted.allocatedDoctorIds || [],
      }
    : undefined;
};

/** The four colours a case block can take. */
export type BoardCardState = 'success' | 'incomplete' | 'manual' | 'empty';

/**
 * Colour for one case block.
 *
 * The rule after a run: STAFFING decides the colour, a pin does not. Green needs (1) a doctor on the room AND
 * (2) the case actually COVERED — the person physically in the room (the CRNA if supervised, or the SOLO
 * doctor if there is no CRNA/student) is on shift for the whole case. So a room whose shift-work CRNA works
 * until 3pm shows green through 1500 and no longer once that CRNA has gone home, even though the supervising
 * doctor (who is covering other rooms) is still around. `covered` is computed by the caller, which knows the
 * case times and each person's shift; it defaults to true so callers without that context keep the old
 * has-a-doctor behaviour. A pin is drawn as a small corner dot instead (`pinned` on BoardScheduleBlock).
 *
 * Before any run there is no staffing to show, so a pin is still blue — it is the only cue that something
 * is staged for the next Allocate.
 */
export const boardCardState = (args: {
  hasPersistedAllocations: boolean;
  manualAllocation: PinAllocationLike | null | undefined;
  allocation: PersistedAllocationLike | null | undefined;
  covered?: boolean;
}): BoardCardState => {
  const { hasPersistedAllocations, manualAllocation, allocation, covered = true } = args;
  if (!hasPersistedAllocations) return manualAllocation ? 'manual' : 'empty';
  // A staged pin shadows the persisted allocation: it is what the next run will hold fixed, so it is the
  // truer picture of who is on the room.
  const effective = manualAllocation || allocation;
  if (!effective) return 'incomplete';
  if ((effective.allocatedDoctorIds?.length || 0) === 0) return 'incomplete';   // no doctor yet
  return covered ? 'success' : 'incomplete';                                    // green only while covered
};

/**
 * Doctors who supervise a CRNA/student ANYWHERE on the board that day.
 *
 * Such a doctor is running other rooms concurrently, so an extra room they merely hold the signature on is
 * not a finished 1:1 solo room — it still NEEDS a CRNA. Only a doctor who supervises nobody all day is a
 * genuine solo doctor whose empty room reads "NONE" rather than "NEED CRNA".
 */
/**
 * Wipe EVERY per-day board overlay for one date. Each overlay is stored under `oc-<name>-<YYYY-MM-DD>`
 * (room/nurse transfers, relief, floats, site locks, dispositions/in-times, case markers, slot marks, move
 * log/undo, …). A fresh simulation replaces the whole day, so it must clear all of them or stale hand-edits
 * linger and mis-render against the new data (e.g. a stale room-transfer hiding a doctor's early rooms).
 *
 * The sweep is by key SUFFIX, so any overlay added later that follows the convention is caught automatically —
 * no per-key list to keep in sync. Non-dated board prefs (`oc-board-algorithm`, `oc-crna-view-mode`,
 * `oc-board-selected-date`, …) never end in a date, so they are left untouched. `store` is injectable for tests.
 */
export const clearAllDayOverlays = (
  date: string,
  store: Pick<Storage, 'key' | 'length' | 'removeItem'> = localStorage,
): void => {
  const suffix = `-${date}`;
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && k.startsWith('oc-') && k.endsWith(suffix)) keys.push(k);
  }
  for (const k of keys) { try { store.removeItem(k); } catch { /* best-effort */ } }
};

export const daySupervisingDoctorIds = (
  allocations: ReadonlyArray<{ allocatedNurseIds?: string[]; allocatedDoctorIds?: string[] }>,
): Set<string> => {
  const ids = new Set<string>();
  for (const a of allocations || []) {
    if ((a?.allocatedNurseIds?.length || 0) === 0) continue;
    for (const id of a.allocatedDoctorIds || []) ids.add(id);
  }
  return ids;
};

export const formatShiftRange = (
  startTimeUtc: string,
  endTimeUtc: string,
  formatTime: (utc: string) => string
): string => {
  return `${formatTime(startTimeUtc)} – ${formatTime(endTimeUtc)}`;
};

// ---------------------------------------------------------------------------
// Schedule hover details
//
// Builds the structured data shown when a user hovers a schedule block on the
// board: which doctors and nurses/students are on the case, each one's shift
// window, pecking order (for ordered doctors), and home hospital. Kept pure so
// the formatting/markup layer (BoardScheduleBlock) stays trivial and this logic
// is unit-testable without a DOM.
// ---------------------------------------------------------------------------

/** Where the staff list came from: a generated allocation, a pending manual
 *  allocation (before the algorithm has run), or nothing assigned. */
export type ScheduleStaffSource = 'allocated' | 'manual' | 'none';

/** Minimal shape of an employee needed to describe them in the hover tooltip. */
export interface HoverEmployeeLike {
  firstName: string;
  lastName: string;
  employeeType: string;
  homeHospitalId?: string;
}

/** Minimal shape of a shift needed for the hover tooltip. */
export interface HoverShiftLike {
  startTimeUtc: string;
  endTimeUtc: string;
  order?: number;
}

/** One staff member rendered in the tooltip. */
export interface HoverStaffDetail {
  employeeId: string;
  name: string;
  typeLabel: string;
  employeeType: string; // raw type ('nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior')
  isDoctor: boolean;
  /** Pecking order for an ordered doctor, otherwise null. */
  order: number | null;
  /** A doctor working a shift with no pecking order ("shift doctor"). */
  isShiftDoctor: boolean;
  shiftStartUtc?: string;
  shiftEndUtc?: string;
  homeHospitalName?: string;
}

/** Everything the hover tooltip needs for a single schedule block. */
export interface ScheduleHoverDetails {
  surgeryType: string;
  startTimeUtc: string;
  endTimeUtc: string;
  source: ScheduleStaffSource;
  doctors: HoverStaffDetail[];
  nurses: HoverStaffDetail[];
  totalStaff: number;
}

const buildStaffDetail = (
  employeeId: string,
  isDoctor: boolean,
  employeesById: ReadonlyMap<string, HoverEmployeeLike>,
  shiftsByEmployeeId: ReadonlyMap<string, HoverShiftLike>,
  hospitalNamesById: ReadonlyMap<string, string>,
  typeDisplayName: (employeeType: string) => string
): HoverStaffDetail => {
  const emp = employeesById.get(employeeId);
  const shift = shiftsByEmployeeId.get(employeeId);
  // Pecking order is a doctor-only concept; ignore any stray order on non-doctors.
  const hasOrder = isDoctor && shift?.order !== undefined && shift?.order !== null;
  const homeHospitalName = emp?.homeHospitalId ? hospitalNamesById.get(emp.homeHospitalId) : undefined;
  return {
    employeeId,
    name: emp ? `${emp.firstName} ${emp.lastName}` : employeeId,
    typeLabel: emp ? typeDisplayName(emp.employeeType) : '',
    employeeType: emp ? emp.employeeType : '',
    isDoctor,
    order: hasOrder ? (shift!.order as number) : null,
    isShiftDoctor: isDoctor && !hasOrder,
    shiftStartUtc: shift?.startTimeUtc,
    shiftEndUtc: shift?.endTimeUtc,
    homeHospitalName: homeHospitalName || undefined,
  };
};

export const buildScheduleHoverDetails = (
  schedule: { surgeryType: string; startTimeUtc: string; endTimeUtc: string },
  doctorIds: readonly string[],
  nurseIds: readonly string[],
  source: ScheduleStaffSource,
  employeesById: ReadonlyMap<string, HoverEmployeeLike>,
  shiftsByEmployeeId: ReadonlyMap<string, HoverShiftLike>,
  hospitalNamesById: ReadonlyMap<string, string>,
  typeDisplayName: (employeeType: string) => string
): ScheduleHoverDetails => {
  const doctors = doctorIds.map((id) =>
    buildStaffDetail(id, true, employeesById, shiftsByEmployeeId, hospitalNamesById, typeDisplayName)
  );
  const nurses = nurseIds.map((id) =>
    buildStaffDetail(id, false, employeesById, shiftsByEmployeeId, hospitalNamesById, typeDisplayName)
  );
  return {
    surgeryType: schedule.surgeryType,
    startTimeUtc: schedule.startTimeUtc,
    endTimeUtc: schedule.endTimeUtc,
    source,
    doctors,
    nurses,
    totalStaff: doctors.length + nurses.length,
  };
};

// Display ordering for employee types in the Board's shift listings: clinical
// seniority (doctors → CRNAs → senior students → junior students). Types not
// in this map sort to the end.
export const SHIFT_VIEW_TYPE_ORDER: Record<string, number> = {
  doctor: 0,
  nurse: 1,
  'nurse-student-senior': 2,
  'nurse-student-junior': 3,
};

// Comparator used by Available / Allocated / All shift views so all three sort
// the same way: type first (per SHIFT_VIEW_TYPE_ORDER), then earliest shift
// end time within a type group. Invalid / unknown end times sort after valid
// ones so missing data does not pull rows to the top of a group.
export const compareShiftRowsByTypeThenEnd = (
  a: { employeeType: string; endTimeUtc: string },
  b: { employeeType: string; endTimeUtc: string }
): number => {
  const ta = SHIFT_VIEW_TYPE_ORDER[a.employeeType] ?? 99;
  const tb = SHIFT_VIEW_TYPE_ORDER[b.employeeType] ?? 99;
  if (ta !== tb) return ta - tb;
  const ea = new Date(a.endTimeUtc).getTime();
  const eb = new Date(b.endTimeUtc).getTime();
  if (Number.isNaN(ea) && Number.isNaN(eb)) return 0;
  if (Number.isNaN(ea)) return 1;
  if (Number.isNaN(eb)) return -1;
  return ea - eb;
};

// ---------------------------------------------------------------------------
// "The" shift for a provider on the board's day
//
// GET /shifts returns every shift that OVERLAPS the day window, in DynamoDB
// index order — so one provider can legitimately have more than one row (a
// night shift carried in from yesterday plus today's day shift), and the array
// order says nothing about which is which. Anything that shows a provider's
// shift therefore has to pick deliberately; the bullpen groups, labels and
// colors a brick by the EARLIEST shift, so the card that brick opens must
// resolve the same one or a brick disagrees with its own card.
//
// Rows with no start time sort last (missing data must never win the pick).
export const earliestShiftByEmployee = <T extends { employeeId: string; startTimeUtc?: string }>(
  shifts: readonly T[]
): Map<string, T> => {
  const byEmployee = new Map<string, T>();
  for (const shift of shifts) {
    const current = byEmployee.get(shift.employeeId);
    if (!current) { byEmployee.set(shift.employeeId, shift); continue; }
    const next = shift.startTimeUtc ?? '';
    const held = current.startTimeUtc ?? '';
    // ISO-8601 UTC strings compare correctly as strings; '' (no start) loses to any real timestamp.
    if (next && (!held || next < held)) byEmployee.set(shift.employeeId, shift);
  }
  return byEmployee;
};

// The pecking-order number actually in force for a provider: a manual reorder
// on the Anesthesiologists tab (persisted per day, mirrored into the board as
// `peckingOrderOverride`) wins over the order stored on the shift itself.
// Returns undefined for "not in the pecking order", which is what the provider
// card treats as a shift-work provider.
export const effectivePeckingOrder = (
  override: Readonly<Record<string, number>> | undefined,
  employeeId: string,
  shiftOrder?: number | null
): number | undefined => {
  const manual = override?.[employeeId];
  if (manual !== undefined && manual !== null) return manual;
  return shiftOrder === undefined || shiftOrder === null ? undefined : shiftOrder;
};

// The `shift` prop for EmployeeDetailsPopover. The card formats each end with
// `new Date(...)`, which renders the literal string "Invalid Date" for a
// missing one, so a half-populated shift must be withheld entirely rather than
// passed through. The order carried is the EFFECTIVE pecking order, so the
// card's "Priority Order #N" row (and its "Out: TBD") always agree with the
// number printed on the bullpen brick.
export const providerCardShift = (
  shift: { startTimeUtc?: string; endTimeUtc?: string } | undefined,
  order: number | undefined
): { startTimeUtc: string; endTimeUtc: string; order?: number } | undefined => {
  if (!shift?.startTimeUtc || !shift?.endTimeUtc) return undefined;
  return { startTimeUtc: shift.startTimeUtc, endTimeUtc: shift.endTimeUtc, order };
};

export interface CaseFlagState {
  auto: boolean;
  overridden: boolean;
  value: boolean;
}

export interface EffectiveCaseFlags {
  nerveBlock: CaseFlagState;
  pediatric: CaseFlagState;
  hasOverride: boolean;
}

// Effective case-marker flags for a schedule: a hand-set override (caseMarkers.ts) wins over
// auto-detection. Auto nerve-block comes from surgery-type keywords (needsNerveBlock). Pediatric has
// NO auto-detection: we deliberately never collect or persist patient age from customer data (see
// backend/src/utils/jsonp-parser.ts, which explicitly skips that column on import), so the pediatric
// flag is manual-only until a runner hand-flags it on the board.
export const effectiveCaseFlags = (
  schedule: { scheduleId: string; surgeryType: string },
  caseMarkers: Record<string, CaseMarker>
): EffectiveCaseFlags => {
  const m = getCaseMarker(caseMarkers, schedule.scheduleId);
  const nbAuto = needsNerveBlock(schedule.surgeryType);
  return {
    nerveBlock: { auto: nbAuto, overridden: m?.nerveBlock !== undefined, value: m?.nerveBlock ?? nbAuto },
    pediatric: { auto: false, overridden: m?.pediatric !== undefined, value: m?.pediatric ?? false },
    hasOverride: !!m,
  };
};

// ---------------------------------------------------------------------------
// Board site (hospital column) order
//
// The Board groups its hospital COLUMNS in the order hospitals first appear in
// the operating-rooms list — `operatingRoomsByHospital` is a first-seen-key
// reduce, and the columns render in that key order with no sort. That order is
// an emergent property of the operating-rooms scan, but it IS what the runner
// sees, so the CRNA / Anesthesiologists tabs must group their sites the SAME
// way (otherwise a tab leads with Downtown while the board leads with Uptown).
//
// `hospitalOrderFromRooms` returns hospitalIds in that first-seen order; feed it
// the SAME operating-rooms array the board uses. `compareByHospitalOrder` turns
// that into a site comparator: known ids sort by their board index, unknown ids
// (a hospital with no rooms in the list) sort AFTER all known ones — matching how
// the board appends its synthetic "missing" column last — then by a caller
// fallback (e.g. name) so the tail stays stable.
// ---------------------------------------------------------------------------
export const hospitalOrderFromRooms = (rooms: readonly { hospitalId?: string }[]): string[] => {
  const order: string[] = [];
  const seen = new Set<string>();
  for (const r of rooms) {
    const hid = r.hospitalId;
    if (hid && !seen.has(hid)) { seen.add(hid); order.push(hid); }
  }
  return order;
};

export const compareByHospitalOrder = (
  order: readonly string[],
  fallback?: (a: string, b: string) => number
): ((a: string, b: string) => number) => {
  const rank = new Map<string, number>();
  order.forEach((hid, i) => rank.set(hid, i));
  const rankOf = (hid: string) => (rank.has(hid) ? rank.get(hid)! : Number.MAX_SAFE_INTEGER);
  return (a: string, b: string): number => {
    const diff = rankOf(a) - rankOf(b);
    if (diff !== 0) return diff;
    return fallback ? fallback(a, b) : 0;
  };
};

// ---------------------------------------------------------------------------
// The "Who's free?" time bar — geometry and drag rules
//
// The bar is the blue live "now" line; grabbing it parks it (glowing purple) and
// swaps the bullpen for the time-scoped availability panel. It is grabbed by ONE
// small knob, and that is a deliberate product decision (Braden, 2026-08-09):
// an interim fix made the WHOLE visible bar grabbable with a full-height strip,
// but that strip sat on top of the case blocks in whichever column the bar was
// parked in — at the 07:00 default, the busiest column of the day — so a click
// aimed at a 7am case hit a dead strip instead of opening the case.
//
// The knob must sit BELOW the board's sticky table header (`position: sticky`,
// z-index 6, 40px tall). That header covering the bar's original top-of-board
// handle is why the bar could not be grabbed at all — the bug this geometry
// exists to prevent, and the reason the knob is not simply pinned to top: 0.
//
// "Below" has to hold WHILE SCROLLING too: `.board-table` is its own vertical
// scroller (App.css) and the knob lives in its scrolling content, so BoardView
// adds the container's scrollTop to the offset below. A fixed offset alone puts
// the knob back under the header on any hospital tall enough to scroll.
// ---------------------------------------------------------------------------
// This must equal `.board-table th/td { height }` in App.css — whosFreeTimeBar.test.ts
// reads the stylesheet and fails if they drift, because two constants agreeing with
// each other in this file would prove nothing about the rendered header.
export const BOARD_STICKY_HEADER_PX = 40;
export const WHOS_FREE_HANDLE_TOP_PX = 42; // clears the sticky header (see above)
export const WHOS_FREE_HANDLE_HIT_PX = 24; // clickable box; the visible dot inside it is smaller

// Timeline geometry: the board maps a time to an x offset as
// `220px (sticky OR column) + 100px per hour` — see `getIndicatorLeftPx` in
// BoardView, which builds the same number from these constants. Dragging the bar
// has to invert it, so both directions read from here and cannot drift apart.
export const BOARD_OR_COLUMN_PX = 220;
export const BOARD_HOUR_COLUMN_PX = 100;

// x is in `.board-table-inner` CONTENT space (measured from the element's own left
// edge), so a horizontally-scrolled board needs no correction. Snaps to 15 minutes
// and clamps to the day, because the pointer can be dragged past either end of the
// timeline and a time outside 00:00–24:00 would place the bar off the board.
export const barMinutesFromContentX = (x: number): number => {
  const raw = ((x - BOARD_OR_COLUMN_PX) / BOARD_HOUR_COLUMN_PX) * 60;
  const snapped = Math.round(raw / 15) * 15;
  return Math.max(0, Math.min(24 * 60, snapped));
};

// Entering "Who's free?" stops live-clock tracking, opens the bullpen and swaps it
// for the availability panel — a heavyweight mode change to trigger by accident.
// The knob is small but it still sits over the board, so mousedown alone must NOT
// take over: wait until the pointer has actually travelled. A plain click on the
// knob therefore leaves the board exactly as it was.
export const BAR_DRAG_THRESHOLD_PX = 4;
export const barDragHasStarted = (originClientX: number, currentClientX: number): boolean =>
  Math.abs(currentClientX - originClientX) >= BAR_DRAG_THRESHOLD_PX;

// Hospital-level (SITE) credentialing — Braden 2026-08-29: "if they aren't credentialed at that hospital or
// surgery center, they can't be in ANY room there — CRNA, doc, or student."
//
// THREE implementations of this one rule have to agree, or the same provider gets three different answers on the
// same screen: Symphony's `siteCredentialingOk` (backend, decides who the ALGORITHM seats),
// `ManualAllocationModal` (decides who the picker OFFERS), and BoardView (decides which manual drops/transfers are
// ALLOWED). This is the frontend's single copy — both callers route through it. The shared contract:
//   - a credential names a site by hospitalId (migrated) OR by hospital NAME (legacy), so both must resolve;
//   - NO credentials on file => eligible EVERYWHERE. This is the ordinary state of a newly added employee, not an
//     edge case: create-employee omits the `credentials` attribute entirely when the list is empty. Drop this
//     escape and the board rejects every placement of such a provider while the allocator happily seats them.
//   - an unknown or absent hospital => don't block (benefit of the doubt).
// `resolveHospitalId` maps one credential string to the hospitalId it names, or undefined if it names no site.
export const isCredentialedAtHospital = (
  credentials: readonly string[] | null | undefined,
  hospitalId: string | null | undefined,
  resolveHospitalId: (credential: string) => string | undefined,
): boolean => {
  if (!hospitalId) return true;
  const creds = credentials || [];
  if (creds.length === 0) return true;
  return creds.some(c => resolveHospitalId(c) === hospitalId);
};
